import React from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy } from 'react'
import PrivateRoute from './PrivateRoute'
import Login from './login/Login'
import Loader from './Loader'
import PageNotFound from './PageNotFound'
import { ProvideAuth } from './hooks/check-auth'
import AppContainer from './AppContainer'
import ForgotPassword from './ForgotPassword'
import ResetPassword from './ResetPassword'
import './App.css'

const Loading = () => <Loader container />

const Organization = lazy(() => import('./Organization'))
const OrganizationConnections = lazy(() => import('./OrganizationConnections'))
const SelfProvisionPage = lazy(
  () => import('./SelfProvisionPage/SelfProvisionPage')
)
const OrganizationOIDCConnection = lazy(
  () => import('./OrganizationOIDCConnection')
)
const OrganizationSAMLConnection = lazy(
  () => import('./OrganizationSAMLConnection')
)
const LicenseView = lazy(() => import('./LicenseDetails'))

function App() {
  const queryClient = new QueryClient()
  return (
    <QueryClientProvider client={queryClient}>
      <ProvideAuth>
        <Router
          basename={process.env.REACT_APP_BASENAME || ''}
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/organization" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/password-reset" element={<ResetPassword />} />

            {/* Wrap AppContainer around nested routes */}
            <Route element={<AppContainer />}>
              <Route
                path="/organization"
                element={
                  <PrivateRoute>
                    <Suspense fallback={<Loading />}>
                      <Organization />
                    </Suspense>
                  </PrivateRoute>
                }
              />
              <Route
                path="/organization/:organizationId/sso"
                element={
                  <PrivateRoute>
                    <Suspense fallback={<Loading />}>
                      <OrganizationConnections />
                    </Suspense>
                  </PrivateRoute>
                }
              />
              <Route
                path="/organization/:organizationId/sso/oidc"
                element={
                  <PrivateRoute>
                    <Suspense fallback={<Loading />}>
                      <OrganizationOIDCConnection />
                    </Suspense>
                  </PrivateRoute>
                }
              />
              <Route
                path="/organization/:organizationId/sso/saml"
                element={
                  <PrivateRoute>
                    <Suspense fallback={<Loading />}>
                      <OrganizationSAMLConnection />
                    </Suspense>
                  </PrivateRoute>
                }
              />
              <Route
                path="/organization/:organizationId/provision"
                element={
                  <PrivateRoute>
                    <Suspense fallback={<Loading />}>
                      <SelfProvisionPage />
                    </Suspense>
                  </PrivateRoute>
                }
              />
              <Route
                path="/organization/:organizationId/license/:licenseId"
                element={
                  <PrivateRoute>
                    <Suspense fallback={<Loading />}>
                      <LicenseView />
                    </Suspense>
                  </PrivateRoute>
                }
              />

              <Route
                path="*"
                element={
                  <PrivateRoute>
                    <PageNotFound />
                  </PrivateRoute>
                }
              />
            </Route>
          </Routes>
        </Router>
      </ProvideAuth>
    </QueryClientProvider>
  )
}

export default App
